import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { NumericFormat } from 'react-number-format';
// material
import {
  Stack,
  TextField,
  FormControl,
  Select,
  InputLabel,
  FormHelperText,
  MenuItem,
  Grid,
  InputAdornment,
  Divider,
  Chip,
  Tab,
  Box
} from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
// contexts
import { TicketsContext } from '../../../contexts/Tickets';

TicketFormBasic.propTypes = {
  formik: PropTypes.object.isRequired
};

export default function TicketFormBasic({ formik }) {
  const { initialValues, errors, touched, getFieldProps, setFieldValue } = formik;
  const { sections } = useContext(TicketsContext);

  const generateReference = (name) => {
    const token = Math.random().toString(36).substr(2, 9).toUpperCase();

    const code = name
      .split(' ')
      .map((n) => n.charAt(0).toUpperCase())
      .join('');

    if (!initialValues.reference) {
      setFieldValue('reference', `TKT-${code}-${token}`);
    }
  };

  return (
    <Stack spacing={2}>
      {Boolean(sections.length && !sections[0].single) && (
        <FormControl fullWidth>
          <InputLabel>Section*</InputLabel>
          <Select
            label="Section*"
            {...getFieldProps('sectionId')}
            error={Boolean(touched.sectionId && errors.sectionId)}
            disabled={Boolean(initialValues.sectionId)}
          >
            {sections.map((section) => (
              <MenuItem key={section._id} value={section._id}>
                {section.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText error={Boolean(touched.sectionId && errors.sectionId)}>
            {touched.sectionId && errors.sectionId}
          </FormHelperText>
        </FormControl>
      )}

      <TextField
        fullWidth
        label="[EN] Name*"
        placeholder="i.e. General Admission"
        {...getFieldProps('name')}
        onChange={(e) => {
          const name = e.target.value;
          generateReference(name);
          setFieldValue('name', name);
        }}
        error={Boolean(touched.name && errors.name)}
        helperText={touched.name && errors.name}
      />

      {Boolean(!sections.length || (sections.length && sections[0].single)) && (
        <TextField
          fullWidth
          label="[EN] Description (optional)"
          multiline
          rows={4}
          {...getFieldProps('description')}
          error={Boolean(touched.description && errors.description)}
          helperText={touched.description && errors.description}
        />
      )}

      <TextField
        fullWidth
        label="Reference*"
        placeholder="i.e. TKT001"
        {...getFieldProps('reference')}
        disabled={Boolean(initialValues.reference)}
        error={Boolean(touched.reference && errors.reference)}
        helperText={touched.reference && errors.reference}
      />

      <Grid container>
        <Grid item xs={6} pr={1}>
          <NumericFormat
            fullWidth
            label="Limit*"
            {...getFieldProps('limit')}
            error={Boolean(touched.limit && errors.limit)}
            helperText={touched.limit && errors.limit}
            decimalScale={0}
            customInput={TextField}
          />
        </Grid>
        <Grid item xs={6} pl={1}>
          <NumericFormat
            fullWidth
            label="Price*"
            {...getFieldProps('price')}
            error={Boolean(touched.price && errors.price)}
            helperText={touched.price && errors.price}
            decimalScale={2}
            allowedDecimalSeparators={[',']}
            customInput={TextField}
            InputProps={{
              startAdornment: <InputAdornment position="start">€</InputAdornment>
            }}
          />
        </Grid>
      </Grid>

      <Divider>
        <Chip label="Translations" size="small" />
      </Divider>

      <Box sx={{ width: '100%', typography: 'body1', marginTop: '0 !important' }}>
        <TabContext value="pt">
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <TabList>
              <Tab label="PT" value="pt" />
            </TabList>
          </Box>
          <TabPanel value="pt">
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="[PT] Name"
                variant="filled"
                {...getFieldProps('translation.pt.name')}
                error={Boolean(touched.translation?.pt?.name && errors.translation?.pt?.name)}
                helperText={touched.translation?.pt?.name && errors.translation?.pt?.name}
              />

              {Boolean(!sections.length || (sections.length && sections[0].single)) && (
                <TextField
                  fullWidth
                  label="[PT] Description (optional)"
                  multiline
                  variant="filled"
                  rows={4}
                  {...getFieldProps('translation.pt.description')}
                  error={Boolean(
                    touched.translation?.pt?.description && errors.translation?.pt?.description
                  )}
                  helperText={
                    touched.translation?.pt?.description && errors.translation?.pt?.description
                  }
                />
              )}
            </Stack>
          </TabPanel>
        </TabContext>
      </Box>
    </Stack>
  );
}
